import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const formatElapsed = (totalSeconds) => {
  const safe = Math.max(0, Math.floor(totalSeconds || 0));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  const mm = String(minutes).padStart(2, "0");
  const ss = String(seconds).padStart(2, "0");
  return hours > 0 ? `${hours}:${mm}:${ss}` : `${minutes}:${ss}`;
};

const formatClock = (ms) =>
  new Date(ms).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

const initialsOf = (name) => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const headlineFor = (call) => {
  switch (call.stage) {
    case "connected":
      return "On call";
    case "completed":
      return "Call ended";
    case "missed":
      return "Missed call";
    case "noanswer":
      return "No answer yet";
    default:
      return call.attempts > 1
        ? `Ringing again, attempt ${call.attempts}`
        : "Ringing on your phone";
  }
};

const timerLabelFor = (call) => {
  switch (call.stage) {
    case "connected":
      return "Talk time";
    case "completed":
      return "Talk time";
    case "missed":
      return "Rang for";
    default:
      return "Since the call arrived";
  }
};

const useElapsed = (since, running) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!since || !running) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [since, running]);

  return since ? Math.max(0, Math.floor((now - since) / 1000)) : 0;
};

const CallCard = ({ call, onDismiss, onOpenLead }) => {
  const isFinished = call.stage === "completed" || call.stage === "missed";
  const timerSince = call.stage === "connected" ? call.connectedAt : call.startedAt;
  const liveSeconds = useElapsed(timerSince, !isFinished);

  let timerSeconds = liveSeconds;
  if (call.stage === "completed") {
    timerSeconds = call.durationSeconds ?? 0;
  } else if (call.stage === "missed") {
    timerSeconds = Math.max(0, Math.round(((call.endedAt || call.startedAt) - call.startedAt) / 1000));
  }

  const displayName = call.name || call.phone || "Unknown caller";
  const canOpenLead = Boolean(onOpenLead && call.leadId);

  return (
    <motion.section
      role="status"
      aria-live="polite"
      aria-label="Live call status"
      initial={{ opacity: 0, y: 32 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 32 }}
      transition={{ duration: 0.22 }}
      className={`lc-card lc-${call.stage}`}
    >
      <div className="lc-head">
        <h2 className="lc-title">
          <span className="lc-beacon" aria-hidden="true" />
          <span>{headlineFor(call)}</span>
        </h2>
        <div className="lc-head-right">
          <div className="lc-timer">
            <div className="lc-timer-value">{formatElapsed(timerSeconds)}</div>
            <div className="lc-timer-label">{timerLabelFor(call)}</div>
          </div>
          <button
            type="button"
            className="lc-close"
            onClick={onDismiss}
            aria-label="Hide call status"
            title="Hide"
          >
            ✕
          </button>
        </div>
      </div>

      <div className="lc-who">
        <div className="lc-avatar" aria-hidden="true">
          {initialsOf(call.name || call.phone)}
        </div>
        <div className="lc-who-text">
          <div className="lc-name">{displayName}</div>
          {call.name && call.phone ? <div className="lc-phone">{call.phone}</div> : null}
          {call.projectName ? <div className="lc-project">{call.projectName}</div> : null}
        </div>
      </div>

      <ol className="lc-steps">
        {call.steps.map((step) => (
          <motion.li
            key={step.id}
            layout
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.2 }}
          >
            <span className={`lc-node lc-node-${step.tone}`} aria-hidden="true" />
            <div>
              <div className="lc-step-label">{step.label}</div>
              {step.detail ? <div className="lc-step-detail">{step.detail}</div> : null}
            </div>
            <time className="lc-step-time">{formatClock(step.at)}</time>
          </motion.li>
        ))}
      </ol>

      {canOpenLead ? (
        <button type="button" className="lc-open" onClick={onOpenLead}>
          Open lead details
        </button>
      ) : !isFinished ? (
        <p className="lc-foot">
          Pick up on your registered phone. The lead details open here once you connect.
        </p>
      ) : null}
    </motion.section>
  );
};

const LiveCallPopup = ({ call, onDismiss, onOpenLead }) => (
  <>
    <AnimatePresence>
      {call ? (
        <CallCard
          key={call.key}
          call={call}
          onDismiss={onDismiss}
          onOpenLead={onOpenLead}
        />
      ) : null}
    </AnimatePresence>

    <style>{`
      .lc-card {
        --lc-accent: #F46653;
        position: fixed;
        right: 20px;
        bottom: 20px;
        z-index: 9999;
        box-sizing: border-box;
        width: 340px;
        max-width: calc(100vw - 24px);
        max-height: calc(100vh - 40px);
        overflow-y: auto;
        padding: 16px 18px 16px;
        border-radius: 16px;
        background: #0C4550;
        color: #fff;
        box-shadow: 0 14px 34px rgba(0, 0, 0, 0.3);
        font-family: 'Plus Jakarta Sans', sans-serif;
      }
      .lc-connected { --lc-accent: #22c55e; }
      .lc-noanswer { --lc-accent: #f5b544; }
      .lc-completed { --lc-accent: #9fb8bd; }
      .lc-missed { --lc-accent: #F46653; }

      .lc-head {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 12px;
      }
      .lc-title {
        display: flex;
        align-items: center;
        gap: 9px;
        margin: 0;
        font-size: 15px;
        font-weight: 700;
        line-height: 1.25;
      }
      .lc-beacon {
        position: relative;
        flex-shrink: 0;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: var(--lc-accent);
      }
      .lc-beacon::after {
        content: "";
        position: absolute;
        inset: 0;
        border-radius: 50%;
        background: var(--lc-accent);
        animation: lc-ping 1.4s ease-out infinite;
      }
      .lc-completed .lc-beacon::after,
      .lc-missed .lc-beacon::after { display: none; }

      .lc-head-right {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        flex-shrink: 0;
      }
      .lc-timer { text-align: right; }
      .lc-timer-value {
        font-size: 20px;
        font-weight: 700;
        line-height: 1;
        font-variant-numeric: tabular-nums;
      }
      .lc-timer-label {
        margin-top: 4px;
        font-size: 10.5px;
        opacity: 0.7;
      }
      .lc-close {
        padding: 4px 6px;
        border: 0;
        border-radius: 6px;
        background: transparent;
        color: rgba(255, 255, 255, 0.75);
        font-size: 14px;
        line-height: 1;
        cursor: pointer;
      }
      .lc-close:hover { color: #fff; }
      .lc-close:focus-visible,
      .lc-open:focus-visible {
        outline: 2px solid #fff;
        outline-offset: 2px;
      }

      .lc-who {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-top: 14px;
      }
      .lc-avatar {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.14);
        font-size: 14px;
        font-weight: 700;
      }
      .lc-who-text { min-width: 0; }
      .lc-name {
        overflow: hidden;
        font-size: 14px;
        font-weight: 700;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
      .lc-phone { margin-top: 2px; font-size: 12px; opacity: 0.8; }
      .lc-project { margin-top: 2px; font-size: 11.5px; opacity: 0.65; }

      .lc-steps {
        margin: 16px 0 0;
        padding: 0;
        list-style: none;
      }
      .lc-steps li {
        position: relative;
        display: grid;
        grid-template-columns: 18px 1fr auto;
        column-gap: 10px;
        padding-bottom: 12px;
      }
      .lc-steps li:last-child { padding-bottom: 0; }
      .lc-steps li:not(:last-child)::before {
        content: "";
        position: absolute;
        left: 8px;
        top: 17px;
        bottom: -1px;
        width: 2px;
        background: rgba(255, 255, 255, 0.2);
      }
      .lc-node {
        position: relative;
        box-sizing: border-box;
        width: 12px;
        height: 12px;
        margin: 3px 0 0 3px;
        border-radius: 50%;
        background: #22c55e;
      }
      .lc-node-warn { background: #f5b544; }
      .lc-node-bad { background: #F46653; }
      .lc-node-live { background: #fff; }
      .lc-node-live::after {
        content: "";
        position: absolute;
        inset: 0;
        border-radius: 50%;
        background: #fff;
        animation: lc-ping 1.4s ease-out infinite;
      }
      .lc-step-label { font-size: 12.5px; font-weight: 600; line-height: 1.35; }
      .lc-step-detail { margin-top: 1px; font-size: 11.5px; opacity: 0.72; line-height: 1.4; }
      .lc-step-time {
        font-size: 11px;
        opacity: 0.65;
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
      }

      .lc-foot {
        margin: 14px 0 0;
        font-size: 11.5px;
        line-height: 1.45;
        opacity: 0.78;
      }
      .lc-open {
        width: 100%;
        margin-top: 14px;
        padding: 9px 12px;
        border: 0;
        border-radius: 10px;
        background: #fff;
        color: #0C4550;
        font: inherit;
        font-size: 12.5px;
        font-weight: 700;
        cursor: pointer;
      }

      @keyframes lc-ping {
        0% { transform: scale(1); opacity: 0.6; }
        100% { transform: scale(2.6); opacity: 0; }
      }
      @media (prefers-reduced-motion: reduce) {
        .lc-beacon::after,
        .lc-node-live::after { animation: none; display: none; }
      }
      @media (max-width: 480px) {
        .lc-card { right: 12px; bottom: 12px; }
      }
    `}</style>
  </>
);

export default LiveCallPopup;
